import { Suspense, lazy } from "react";
import { Navigate, useRoutes, useLocation } from "react-router-dom";
// layouts
import DashboardLayout from "../layouts/dashboard";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
// components
import LoadingScreen from "../components/LoadingScreen";
import AuthGuard from "../guards/AuthGuard";
import { frontendPages } from "./paths";

// ----------------------------------------------------------------------
// eslint-disable-next-line
const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = !frontendPages.includes(pathname);
  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: "fixed",
            }),
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: "/",
      element: (
        <AuthGuard>
          {" "}
          <DashboardLayout />{" "}
        </AuthGuard>
      ),
      children: [
        { path: "", element: <Wallet /> },
        { path: "dashboard", element: <Wallet /> },
        {
          path: "settings",
          children: [
            { path: "", element: <Settings /> },
            { path: ":tab", element: <Settings /> },
          ],
        },
        {
          path: "bookings",
          children: [
            { path: "", element: <Bookings /> },
            { path: ":bookingId", element: <EditBooking /> },
          ],
        },
        { path: "reviews", element: <Reviews /> },
        { path: "messages", element: <Messages /> },
        { path: "wishlist", element: <Wishlist /> },
        { path: "bill-payments", element: <BillPayments /> },
        { path: "referral", element: <Referral /> },
      ],
    },
    // Main Routes
    {
      path: "*",
      element: <LogoOnlyLayout />,
      children: [
        {
          path: "signup",
          children: [
            {
              path: "",
              element: (
                <AuthGuard>
                  <Login />
                </AuthGuard>
              ),
            },
            {
              path: ":ref",
              element: (
                <AuthGuard>
                  <Login />
                </AuthGuard>
              ),
            },
          ],
        },
        { path: "404", element: <NotFound /> },
        { path: "500", element: <ErrorOccured /> },
        { path: "*", element: <Navigate to="/404" replace /> },
        {
          path: "login",
          element: (
            <AuthGuard>
              <Login />
            </AuthGuard>
          ),
        },
        { path: "reset-password", element: <ResetPassword /> },
        { path: "forgot-password", element: <ForgotPassword /> },
        {
          path: "verify",
          element: (
            <AuthGuard>
              <VerifyCode />
            </AuthGuard>
          ),
        },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

// IMPORT COMPONENTS

// admin pages
const Referral = Loadable(lazy(() => import("../pages/dashboard/Referral")));
const Settings = Loadable(lazy(() => import("../pages/dashboard/Settings")));
const Wallet = Loadable(lazy(() => import("../pages/dashboard/Wallet")));
const Messages = Loadable(lazy(() => import("../pages/dashboard/Messages")));
const Reviews = Loadable(lazy(() => import("../pages/dashboard/Reviews")));
const Bookings = Loadable(lazy(() => import("../pages/dashboard/Bookings")));
const Wishlist = Loadable(lazy(() => import("../pages/dashboard/Wishlist")));
const ErrorOccured = Loadable(
  lazy(() => import("../pages/errors/ErrorOccured")),
);
const NotFound = Loadable(lazy(() => import("../pages/errors/NotFound")));

// auth
const VerifyCode = Loadable(
  lazy(() => import("../pages/authentication/Verify")),
);
const ForgotPassword = Loadable(
  lazy(() => import("../pages/authentication/ForgotPassword")),
);
const ResetPassword = Loadable(
  lazy(() => import("../pages/authentication/ResetPassword")),
);
const Login = Loadable(lazy(() => import("../pages/authentication/Login")));
const BillPayments = Loadable(
  lazy(() => import("../pages/dashboard/BillPayments")),
);
const EditBooking = Loadable(
  lazy(() => import("../pages/dashboard/EditBooking")),
);
